// VOY-485 (rebuild) — Search behavior toggles (Settings tab).
//
// Five boolean toggles distinct from the categorical Travel preferences
// card. These are SEARCH-DEFAULT toggles (how Voy plans on the user's
// behalf), not the user's traveler-identity preferences. The audit flagged
// the name collision; this section is renamed "Search behavior" to keep
// the categorical card free to own the "Travel preferences" label as
// the live profile does.
//
// Storage: `user_preferences` preference_type='travel_behavior'. Separate
// row from 'travel' (categorical) and 'locale' (currency/locale).

import { useEffect, useState, type ChangeEvent } from 'react'
import { getPreferences, savePreferences } from '@/lib/profile'

export interface TravelBehaviorTogglesSectionProps {
  userId: string
}

interface BehaviorPrefs {
  auto_apply_my_style?: boolean
  skip_non_refundable_hotels?: boolean
  bundle_flights_hotel?: boolean
  show_carbon_estimate?: boolean
  use_passport_fast_checkout?: boolean
}

const TOGGLES: { key: keyof BehaviorPrefs; label: string; hint?: string; defaultValue?: boolean }[] = [
  { key: 'auto_apply_my_style', label: 'Auto-apply my style to every search', hint: 'When Voy plans for you', defaultValue: true },
  { key: 'skip_non_refundable_hotels', label: 'Skip non-refundable hotels', defaultValue: true },
  { key: 'bundle_flights_hotel', label: 'Bundle flights + hotel by default' },
  { key: 'show_carbon_estimate', label: 'Show carbon estimate', defaultValue: true },
  { key: 'use_passport_fast_checkout', label: 'Use my passport for fast checkout', defaultValue: true },
]

export function TravelBehaviorTogglesSection({ userId }: TravelBehaviorTogglesSectionProps) {
  const [prefs, setPrefs] = useState<BehaviorPrefs>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<keyof BehaviorPrefs | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const data = await getPreferences(userId, 'travel_behavior')
        if (!cancelled) setPrefs((data as BehaviorPrefs) ?? {})
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [userId])

  async function toggle(key: keyof BehaviorPrefs, e: ChangeEvent<HTMLInputElement>) {
    const next = { ...prefs, [key]: e.target.checked }
    setPrefs(next)
    setSaving(key)
    try {
      await savePreferences(userId, 'travel_behavior', next as Record<string, unknown>)
    } catch (err) {
      console.error('[TravelBehaviorTogglesSection] save failed:', err)
    } finally {
      setSaving(null)
    }
  }

  return (
    <section className="card" aria-labelledby="search-behavior-h">
      <div className="card-h">
        <h3 id="search-behavior-h">Search behavior</h3>
        {saving && <span className="meta">SAVING…</span>}
      </div>
      <div className="stack sm">
        {TOGGLES.map(({ key, label, hint, defaultValue }) => {
          const checked = prefs[key] ?? Boolean(defaultValue)
          return (
            <div className="toggle-row" key={key}>
              <div>
                <div className="toggle-row-label">{label}</div>
                {hint && <div className="toggle-row-hint">{hint}</div>}
              </div>
              <label className="switch">
                <input type="checkbox" checked={checked} disabled={loading} aria-label={label} onChange={(e) => void toggle(key, e)} />
                <span className="track" />
              </label>
            </div>
          )
        })}
      </div>
    </section>
  )
}
